/**
 * OptionsUi — shared, theme-tokenized building blocks for the unified Options
 * experience (hub, monitor, builder). Replaces the ad-hoc `Metric` components and
 * hardcoded hex colors scattered across the option components, and adds the
 * plain-English scaffolding a beginner needs: <Term> (hover-to-explain jargon),
 * <OptionMetric>, <RiskMeter>, <PlainStatus>, money helpers.
 */
import React from 'react';
import { Box, Typography, Popover, useTheme } from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { glossaryLookup } from '../../../utils/optionsGlossary';

/** Semantic colors, theme-aware — use these instead of raw hex so dark mode + brand stay consistent. */
export function useOptionColors() {
  const t = useTheme();
  const dark = t.palette.mode === 'dark';
  return {
    profit: dark ? '#4caf50' : '#2e7d32',
    loss: dark ? '#ef5350' : '#c62828',
    warn: dark ? '#ffb74d' : '#ed6c02',
    neutral: dark ? '#90a4ae' : '#607d8b',
    profitBg: dark ? 'rgba(76,175,80,0.16)' : 'rgba(46,125,50,0.10)',
    lossBg: dark ? 'rgba(239,83,80,0.16)' : 'rgba(198,40,40,0.10)',
    warnBg: dark ? 'rgba(255,183,77,0.16)' : 'rgba(237,108,2,0.10)',
    accent: t.palette.primary.main,
  };
}

/**
 * useAccountCapital — the user's total portfolio value (INR) from the dashboard
 * summary, so risk can be framed as a % of REAL capital (not a per-symbol budget).
 * Module-cached so it's fetched once per session and shared across all callers.
 */
let _capitalCache: number | null = null;
export function useAccountCapital(): number | null {
  const [cap, setCap] = React.useState<number | null>(_capitalCache);
  React.useEffect(() => {
    if (_capitalCache != null) { setCap(_capitalCache); return; }
    let cancel = false;
    import('../../../services/dashboard.service').then(({ dashboardService }) =>
      dashboardService.getSummary().then((r: any) => {
        const v = Number(r?.data?.data?.portfolio?.totalValue);
        if (!cancel && Number.isFinite(v) && v > 0) { _capitalCache = v; setCap(v); }
      }).catch(() => { /* ignore — RiskMeter falls back to ₹-only */ }),
    ).catch(() => {});
    return () => { cancel = true; };
  }, []);
  return cap;
}

/** Indian-rupee money helpers. */
export const inr0 = (v: number) => `₹${Math.round(v).toLocaleString('en-IN')}`;
export const inrSigned = (v: number) => `${v >= 0 ? '+' : '−'}₹${Math.abs(Math.round(v)).toLocaleString('en-IN')}`;

/**
 * <Term> — renders a word/phrase with a subtle dotted underline that, on hover or
 * tap, explains the concept in plain English from the glossary. Use it anywhere a
 * beginner might hit jargon: <Term k="fitScore">Fit</Term>.
 */
export const Term: React.FC<{ k: string; children?: React.ReactNode; sx?: any }> = ({ k, children, sx }) => {
  const [anchor, setAnchor] = React.useState<HTMLElement | null>(null);
  const entry = glossaryLookup(k);
  const label = children ?? entry?.title ?? k;
  if (!entry) return <>{label}</>;
  const open = Boolean(anchor);
  return (
    <>
      <Box
        component="span"
        onMouseEnter={e => setAnchor(e.currentTarget)}
        onMouseLeave={() => setAnchor(null)}
        onClick={e => { e.stopPropagation(); setAnchor(a => (a ? null : e.currentTarget)); }}
        sx={{
          borderBottom: '1px dotted', borderColor: 'text.disabled', cursor: 'help',
          textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 0.2, ...sx,
        }}
      >
        {label}
        <HelpOutlineIcon sx={{ fontSize: 12, opacity: 0.5, mb: '1px' }} />
      </Box>
      <Popover
        open={open}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        disableRestoreFocus
        sx={{ pointerEvents: 'none' }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { p: 1.25, maxWidth: 300, pointerEvents: 'auto' } } }}
      >
        <Typography sx={{ fontWeight: 800, fontSize: '0.8rem', mb: 0.3 }}>{entry.title}</Typography>
        <Typography sx={{ fontSize: '0.75rem', lineHeight: 1.45, color: 'text.secondary' }}>{entry.plain}</Typography>
      </Popover>
    </>
  );
};

/**
 * <OptionMetric> — a labelled figure. The label can itself be a glossary term
 * (pass `term` to make it hover-explainable).
 */
export const OptionMetric: React.FC<{
  label: string; value: React.ReactNode; color?: string; sub?: React.ReactNode; term?: string; align?: 'left' | 'center' | 'right';
}> = ({ label, value, color, sub, term, align = 'left' }) => (
  <Box sx={{ textAlign: align }}>
    <Typography component="div" sx={{ fontSize: '0.58rem', fontWeight: 700, letterSpacing: 0.3, color: 'text.secondary', textTransform: 'uppercase' }}>
      {term ? <Term k={term}>{label}</Term> : label}
    </Typography>
    <Typography sx={{ fontSize: '0.95rem', fontWeight: 800, color: color || 'text.primary', lineHeight: 1.2 }}>{value}</Typography>
    {sub && <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>{sub}</Typography>}
  </Box>
);

/**
 * <RiskMeter> — frames a ₹ risk as a fraction of the user's capital so a beginner
 * grasps "how big is this bet" without doing math. Green ≤ small, amber = moderate,
 * red = large. `capital` may be undefined (then it just shows the ₹ amount).
 */
export const RiskMeter: React.FC<{ riskInr: number; capitalInr?: number | null; compact?: boolean }> = ({ riskInr, capitalInr, compact }) => {
  const c = useOptionColors();
  const pct = capitalInr && capitalInr > 0 ? (Math.abs(riskInr) / capitalInr) * 100 : null;
  const tone = pct == null ? c.neutral : pct <= 2 ? c.profit : pct <= 5 ? c.warn : c.loss;
  const word = pct == null ? '' : pct <= 2 ? 'small' : pct <= 5 ? 'moderate' : 'large';
  const barPct = pct == null ? 0 : Math.max(4, Math.min(100, pct * 10)); // 10% capital fills the bar
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.6 }}>
        <Typography sx={{ fontSize: compact ? '0.72rem' : '0.85rem', fontWeight: 800, color: tone }}>{inr0(Math.abs(riskInr))}</Typography>
        {pct != null && <Typography sx={{ fontSize: '0.62rem', color: tone, fontWeight: 700 }}>{pct.toFixed(1)}% of capital · {word}</Typography>}
      </Box>
      {pct != null && (
        <Box sx={{ mt: 0.3, height: 5, borderRadius: 3, bgcolor: 'action.hover', overflow: 'hidden' }}>
          <Box sx={{ width: `${barPct}%`, height: '100%', bgcolor: tone, borderRadius: 3 }} />
        </Box>
      )}
    </Box>
  );
};

/**
 * <PlainStatus> — a single, colour-coded plain-English line summarising how a
 * position is doing right now, so a beginner reads a sentence, not raw numbers.
 */
export const PlainStatus: React.FC<{ tone: 'good' | 'bad' | 'warn' | 'neutral'; children: React.ReactNode; emoji?: string }> = ({ tone, children, emoji }) => {
  const c = useOptionColors();
  const col = tone === 'good' ? c.profit : tone === 'bad' ? c.loss : tone === 'warn' ? c.warn : c.neutral;
  const bg = tone === 'good' ? c.profitBg : tone === 'bad' ? c.lossBg : tone === 'warn' ? c.warnBg : 'action.hover';
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, px: 1, py: 0.5, borderRadius: 1, bgcolor: bg, borderLeft: `3px solid ${col}` }}>
      {emoji && <Box component="span" sx={{ fontSize: '0.9rem' }}>{emoji}</Box>}
      <Typography sx={{ fontSize: '0.76rem', fontWeight: 700, color: col, lineHeight: 1.35 }}>{children}</Typography>
    </Box>
  );
};

/** Human "time to go" from an epoch-ms deadline. */
export const timeToGo = (ms?: number | null): { label: string; soon: boolean } | null => {
  if (!ms) return null;
  const diff = ms - Date.now();
  if (diff <= 0) return { label: 'settling now', soon: true };
  const h = diff / 3.6e6, d = Math.floor(h / 24), hh = Math.floor(h % 24);
  return { label: d > 0 ? `${d}d ${hh}h` : `${hh}h ${Math.floor((h % 1) * 60)}m`, soon: diff < 6 * 3.6e6 };
};
